import { useDiagnosticDetail } from '@/core/domain/workorder/Diagnostics/Diagnostics.hooks';
import { useWorkOrder } from '@/core/domain/workorder/WorkOrders/WorkOrders.hooks';
import { useWorkorderConfigurationCompany } from '@/core/domain/workorder/WorkordersConfiguration/WorkordersConfiguration.hooks';
import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  WorkorderChecklistContextParams,
  WorkorderChecklistItemType,
} from './WorkorderChecklistProvider.types';
import { ChecklistVehicle } from '@/core/domain/workorder/WorkOrdersChecklist/WorkOrdersChecklist.types';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';

const WorkorderChecklistContext = createContext(
  {} as WorkorderChecklistContextParams,
);

const WorkorderChecklistProvider: React.FC<PropsWithChildren<unknown>> = (
  props,
) => {
  const params = useLocation();
  const { checklistItemType, checklistItemId, workorderId, diagnosticId } =
    Object.fromEntries(new URLSearchParams(params.search)) as {
      checklistItemType?: WorkorderChecklistItemType;
      checklistItemId?: string;
      workorderId?: string;
      diagnosticId?: string;
    };
  const history = useHistory();

  const [vehicleSelected, setVehicleSelected] = useState<ChecklistVehicle>();
  const [customerId, setCustomerId] = useState<number>();

  useEffect(() => {
    if (!checklistItemType) {
      history.push('/404');
    }
  });

  const workOrder = useWorkOrder(Number(workorderId));
  const diagnosticDetail = useDiagnosticDetail(Number(diagnosticId));

  const workorderConfigCompany = useWorkorderConfigurationCompany();

  useEffect(() => {
    (async () => {
      if (
        checklistItemType === WorkorderChecklistItemType.Workorder &&
        workOrder?.data
      ) {
        setVehicleSelected(workOrder.data.vehicle as ChecklistVehicle);
        setCustomerId(workOrder.data.customer?.id);
        return await workorderConfigCompany.mutateAsync(
          workOrder.data.company.id,
        );
      }

      if (
        checklistItemType === WorkorderChecklistItemType.Diagnostic &&
        diagnosticDetail.data
      ) {
        setVehicleSelected(diagnosticDetail.data?.vehicle as ChecklistVehicle);
        setCustomerId(diagnosticDetail.data.customer?.id);
        await workorderConfigCompany.mutateAsync(
          diagnosticDetail.data.company.id,
        );
      }
    })();
  }, [
    workOrder.dataUpdatedAt,
    diagnosticDetail.dataUpdatedAt,
    checklistItemType,
  ]);

  return (
    <WorkorderChecklistContext.Provider
      value={{
        checklistItemId: Number(checklistItemId),
        checklistItemType,
        workorderId: Number(workorderId),
        workorderComplementId:
          checklistItemType === WorkorderChecklistItemType.Workorder
            ? Number(checklistItemId)
            : undefined,
        diagnosticId: diagnosticId ? Number(diagnosticId) : undefined,
        boschChecklist: workorderConfigCompany.data?.boschChecklist,
        vehicleSelected,
        customerId,
      }}
    >
      {workOrder.isLoading && (
        <ModalLoadingOverlay
          messages={
            workOrder.isLoading
              ? ['Carregando informações do veículo...']
              : ['Carregando...']
          }
        />
      )}

      {props.children}
    </WorkorderChecklistContext.Provider>
  );
};

export const useWorkorderChecklistContext = () =>
  useContext(WorkorderChecklistContext);

export default WorkorderChecklistProvider;
